import {
  AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {Chart} from 'chart.js/auto';
import {ChartConfiguration, ChartDataset, TooltipItem} from 'chart.js';
import {RisorseCounter} from '../../../_models/RisorseCounter';
import {isMobileGrafico, legendaResponsive} from '../chart-defaults';

/** Una fetta: una risorsa, oppure «Altre (N)» quando le risorse superano `maxFette` (risorsa assente). */
export interface FettaRisorsa {
  etichetta: string;
  ore: number;
  sessioni: number;
  colore?: string;
  risorsa?: RisorseCounter;
}

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
  standalone: false
})
export class PieChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() bars: RisorseCounter[] = [];
  @Input() colors?: string[];
  @Input() titolo = 'Ore lavorate per risorsa - oggi';
  @Input() testoVuoto = 'Nessun dato disponibile';
  /** Feature 161: totale ore e numero di risorse al centro della ciambella (testo HTML, letto dagli screen reader). */
  @Input() totaleAlCentro = false;
  /** Feature 161: oltre questo numero le risorse più piccole confluiscono in «Altre (N)». */
  @Input() maxFette?: number;
  /** Feature 161: legenda HTML come lista di link al posto di quella di chart.js. */
  @Input() legendaLink = false;
  @Output() risorsaSelezionata = new EventEmitter<RisorseCounter>();
  @ViewChild('pieCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  public chart: Chart<'doughnut', number[], string | number>;
  fette: FettaRisorsa[] = [];
  private viewInitialized = false;
  private readonly zone = inject(NgZone);

  get hasData(): boolean {
    return !!this.bars && this.bars.some(b => (b.workingTimeOfToday ?? 0) > 0);
  }

  get oreTotali(): number {
    return this.fette.reduce((totale, f) => totale + f.ore, 0);
  }

  get numeroRisorse(): number {
    return (this.bars ?? []).filter(b => (b.workingTimeOfToday ?? 0) > 0).length;
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.viewInitialized && (changes['bars'] || changes['maxFette'])) {
      setTimeout(() => this.createChart(), 0);
    }
  }

  ngOnDestroy() {
    this.distruggi();
  }

  createChart(){
    if (!this.canvasRef) {
      return;
    }
    this.distruggi();
    this.fette = calcolaFette(this.bars ?? [], this.maxFette, this.colors);
    if (this.fette.length === 0) {
      return;
    }
    // Fuori dalla zona Angular: vedi BarChartComponent.createChart.
    const canvas = this.canvasRef.nativeElement;
    const configurazione = this.configurazione(this.fette, isMobileGrafico());
    this.chart = this.zone.runOutsideAngular(() => new Chart(canvas, configurazione));
  }

  private distruggi() {
    const chart = this.chart;
    if (chart) this.zone.runOutsideAngular(() => chart.destroy());
    this.chart = null;
  }

  testoFetta(fetta: FettaRisorsa): string {
    return testoFetta(fetta);
  }

  selezionaFetta(fetta: FettaRisorsa) {
    if (fetta.risorsa) this.risorsaSelezionata.emit(fetta.risorsa);
  }

  configurazione(fette: FettaRisorsa[], isMobile: boolean): ChartConfiguration<'doughnut', number[], string | number> {
    const dataset: ChartDataset<'doughnut', number[]> = {label: 'Ore lavorate', data: fette.map(f => f.ore)};
    if (this.colors && this.colors.length > 0) {
      dataset.backgroundColor = fette.map(f => f.colore);
    }
    return {
      type: 'doughnut',
      data: {
        labels: fette.map(f => f.risorsa ? (f.risorsa.risorsaCodice ?? f.risorsa.risorsaId) : f.etichetta),
        datasets: [dataset]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_e, elementi) => {
          if (elementi.length > 0) this.zone.run(() => this.selezionaFetta(fette[elementi[0].index]));
        },
        plugins: {
          legend: this.legendaLink ? {display: false} : legendaResponsive(isMobile, [12, 16]),
          tooltip: {callbacks: {label: (c: TooltipItem<'doughnut'>) => testoFetta(fette[c.dataIndex])}}
        }
      }
    };
  }
}

export function testoFetta(fetta: FettaRisorsa): string {
  return `${fetta.ore.toLocaleString('it-IT', {maximumFractionDigits: 1})} h · ${fetta.sessioni} sessioni`;
}

/**
 * Risorse con ore > 0, nell'ordine ricevuto. Con `maxFette` e più risorse del limite: le più grandi, e il resto
 * in un'unica fetta «Altre (N)» (N = risorse raggruppate). I colori seguono l'ordine delle fette.
 */
export function calcolaFette(bars: RisorseCounter[], maxFette?: number, colori?: string[]): FettaRisorsa[] {
  const valide = bars.filter(b => (b.workingTimeOfToday ?? 0) > 0);
  const fette: FettaRisorsa[] = valide.map(b => ({
    etichetta: String(b.risorsaCodice ?? b.risorsaId), ore: b.workingTimeOfToday / 3600,
    sessioni: b.countWorkingSessions ?? 0, risorsa: b
  }));
  const raggruppate = maxFette && maxFette > 1 && fette.length > maxFette
    ? raggruppaAltre([...fette].sort((a, b) => b.ore - a.ore), maxFette)
    : fette;
  return raggruppate.map((f, i) => ({...f, colore: colori?.length ? colori[i % colori.length] : undefined}));
}

function raggruppaAltre(ordinate: FettaRisorsa[], maxFette: number): FettaRisorsa[] {
  const resto = ordinate.slice(maxFette - 1);
  return [...ordinate.slice(0, maxFette - 1), {
    etichetta: `Altre (${resto.length})`,
    ore: resto.reduce((t, f) => t + f.ore, 0),
    sessioni: resto.reduce((t, f) => t + f.sessioni, 0)
  }];
}
